import { ArrowRight } from '@phosphor-icons/react';
import { Text } from '@fragments-sdk/ui';
import type { DashboardDrillTarget } from './dashboard-brief';
import type {
  AreaCriticality,
  AreaRiskRow,
  DashboardMetrics,
} from './dashboard-surface';
import s from './dashboard.module.scss';

// ── Area Risk band (operational mode) ────────────────────────────
// Open governance debt bucketed by product area. Counts only — real data from
// denormalized finding fields. Criticality is a label (never chroma); per-area
// coverage % is Phase B (unbuilt) and renders as an explicit "coming"
// affordance rather than a faked number.

function criticalityLabel(criticality: AreaCriticality | null): string {
  if (!criticality) return '—';
  return criticality.charAt(0).toUpperCase() + criticality.slice(1);
}

export function AreaRiskBand({
  metrics,
  onNavigate,
}: {
  metrics: DashboardMetrics;
  onNavigate?: (target: DashboardDrillTarget) => void;
}) {
  const rows = metrics.areaRisk;

  return (
    <section className={s.panel} aria-labelledby="dash-area-heading">
      <header className={s.panelHeader}>
        <Text as="h2" id="dash-area-heading" size="sm" weight="semibold">
          Area risk
        </Text>
        {onNavigate ? (
          <button
            type="button"
            className={s.panelLink}
            onClick={() => onNavigate('findings')}
          >
            <Text as="span" size="xs" weight="medium">
              View findings
            </Text>
            <ArrowRight size={12} weight="bold" />
          </button>
        ) : null}
      </header>

      {rows.length > 0 ? (
        <table className={s.areaTable}>
          <thead>
            <tr>
              <th scope="col">Area</th>
              <th scope="col">Criticality</th>
              <th scope="col" className={s.areaNumCol}>
                Open
              </th>
              <th scope="col" className={s.areaNumCol}>
                New
              </th>
              <th scope="col" className={s.areaNumCol}>
                Coverage
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <AreaRow key={row.areaId} row={row} />
            ))}
          </tbody>
        </table>
      ) : (
        <Text as="p" size="sm" color="tertiary" className={s.emptyFeed}>
          No open findings — no area risk to show. Product areas appear here as
          governance findings land.
        </Text>
      )}

      <Text as="p" size="xs" color="tertiary" className={s.areaFootnote}>
        Per-area coverage % is coming. Today this ranks open governance debt by
        area.
      </Text>
    </section>
  );
}

function AreaRow({ row }: { row: AreaRiskRow }) {
  const isUnassigned = row.criticality === null;
  return (
    <tr>
      <th scope="row" className={s.areaNameCell}>
        <Text as="span" size="sm" color={isUnassigned ? 'tertiary' : undefined}>
          {row.areaName}
        </Text>
      </th>
      <td className={s.areaCriticalityCell}>
        {criticalityLabel(row.criticality)}
      </td>
      <td className={s.areaNumCol}>{row.open}</td>
      <td className={s.areaNumCol}>{row.new}</td>
      <td className={s.areaNumCol}>
        <span className={s.areaComing} aria-label="Coverage coming soon">
          Coming
        </span>
      </td>
    </tr>
  );
}
